// Puts several files into one ZIP (no compression: embroidery files are small and already
// dense, and this keeps it simple and fast on a phone). Used so that a post with several
// design files downloads as ONE file: phones' browsers refuse to start many downloads from
// a single tap, so only the first one or two used to arrive.
//
// files: [{ name: "flower.dst", bytes: Uint8Array }]  ->  Blob (application/zip)
// Names are kept as they are (UTF-8, so Punjabi names work); two files with the same name
// get " (2)", " (3)" … before the extension, and "/" or "\" in a name become "_".

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function safeName(name) {
  // eslint-disable-next-line no-control-regex
  const clean = String(name || "").replace(/[\\/]/g, "_").replace(/[ -]/g, "").trim();
  return clean || "file";
}

function uniqueNames(names) {
  const used = new Set();
  return names.map(original => {
    const name = safeName(original);
    if (!used.has(name.toLowerCase())) {
      used.add(name.toLowerCase());
      return name;
    }
    const dot = name.lastIndexOf(".");
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    for (let n = 2; ; n++) {
      const candidate = `${stem} (${n})${ext}`;
      if (!used.has(candidate.toLowerCase())) {
        used.add(candidate.toLowerCase());
        return candidate;
      }
    }
  });
}

export function makeZip(files, now = new Date()) {
  const encoder = new TextEncoder();
  const names = uniqueNames(files.map(f => f.name));

  const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
  const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();

  const parts = [];
  const central = [];
  let offset = 0;

  files.forEach((file, i) => {
    const nameBytes = encoder.encode(names[i]);
    const size = file.bytes.length;
    const crc = crc32(file.bytes);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); // local file header
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, 0x0800, true); // names are UTF-8
    local.setUint16(8, 0, true); // stored, no compression
    local.setUint16(10, dosTime, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true); // no extra field
    parts.push(local.buffer, nameBytes, file.bytes);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true); // central directory header
    entry.setUint16(4, 20, true); // made by
    entry.setUint16(6, 20, true); // version needed
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, dosTime, true);
    entry.setUint16(14, dosDate, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, size, true);
    entry.setUint32(24, size, true);
    entry.setUint16(28, nameBytes.length, true);
    // extra field, comment, disk number, internal and external attributes: all 0
    entry.setUint32(42, offset, true); // where this file's local header starts
    central.push(entry.buffer, nameBytes);

    offset += 30 + nameBytes.length + size;
  });

  const centralSize = central.reduce((sum, part) => sum + (part.byteLength ?? part.length), 0);

  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); // end of central directory
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...parts, ...central, end.buffer], { type: "application/zip" });
}
